import moment from "moment-timezone";

import { sumBufferedHours } from "../shared/utils/buffer";

import {
  LATE_CHECK_IN_GRACE_MINUTES,
  TAssignedShift,
  TRelativeTime,
  TShift,
  TShiftEligibility,
  TVolunteerScanStatus,
} from "./constants";

const SOON_THRESHOLD_MINUTES = 60;

export function getEligibility(
  now: moment.Moment,
  shift: TShift,
  checkedIn: boolean
): TShiftEligibility {
  if (checkedIn) return "checked-in";

  const latestCheckIn = moment(shift.startTime).add(
    LATE_CHECK_IN_GRACE_MINUTES,
    "minutes"
  );
  if (now.isAfter(latestCheckIn)) return "too-late";

  return "eligible";
}

function formatGap(from: moment.Moment, to: moment.Moment): string {
  const minutes = to.diff(from, "minutes");
  if (minutes < 60) return `${Math.max(minutes, 1)}m`;

  if (minutes <= 24 * 60) return `${Math.max(to.diff(from, "hours"), 1)}h`;

  const days = to.diff(from, "days");
  return `${Math.max(days, 1)} ${days === 1 ? "day" : "days"}`;
}

export function getRelativeTime(
  now: moment.Moment,
  shift: TShift
): TRelativeTime {
  const start = moment(shift.startTime);
  const end = moment(shift.endTime);

  if (now.isSameOrAfter(start) && now.isSameOrBefore(end)) {
    return { text: "now", tone: "now" };
  }

  if (now.isBefore(start)) {
    const tone =
      start.diff(now, "minutes") < SOON_THRESHOLD_MINUTES ? "soon" : "later";
    return { text: `in ${formatGap(now, start)}`, tone };
  }

  return { text: `${formatGap(end, now)} ago`, tone: "past" };
}

export function formatHoursCollected(hours: number): string {
  return `${hours} ${hours === 1 ? "hour" : "hours"} collected`;
}

export function getVolunteerScanStatus(
  now: moment.Moment,
  shifts: TAssignedShift[]
): TVolunteerScanStatus {
  const hours = sumBufferedHours(
    shifts.map((shift) => ({
      shiftId: shift.id,
      start: shift.startTime,
      end: shift.endTime,
      checkedIn: shift.eligibility === "checked-in",
    })),
    { checkedInOnly: true, pastOnly: true, now: now.toDate() }
  );

  if (shifts.length === 0) {
    return { label: "No shifts", color: "secondary-light", hours };
  }

  const happeningNow = shifts.filter(
    (shift) =>
      now.isSameOrAfter(shift.startTime) &&
      now.isSameOrBefore(shift.displayEndTime)
  );
  if (happeningNow.length > 0) {
    const checkedIn = happeningNow.every(
      (shift) => shift.eligibility === "checked-in"
    );
    return {
      label: `${checkedIn ? "Checked in" : "Not checked in"} · shift now`,
      color: checkedIn ? "primary-light" : "danger-light",
      hours,
    };
  }

  const nextShift = [...shifts]
    .filter((shift) => now.isBefore(shift.startTime))
    .sort(
      (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf()
    )[0];
  if (nextShift) {
    const relative = getRelativeTime(now, nextShift);
    return {
      label: `Next shift ${relative.text}`,
      color: relative.tone === "soon" ? "warning-light" : "primary-light",
      hours,
    };
  }

  const incompleteCount = shifts.filter(
    (shift) =>
      now.isSameOrAfter(shift.endTime) && shift.eligibility !== "checked-in"
  ).length;
  if (incompleteCount > 0) {
    return {
      label: `${incompleteCount} incomplete ${
        incompleteCount === 1 ? "shift" : "shifts"
      }`,
      color: "danger-light",
      hours,
    };
  }

  return { label: "All shifts completed", color: "success-light", hours };
}
